import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import { Button, Paper, TextField, CardActions } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { AppState, AppThunkDispatch } from 'src/store';
import { getScopeReadyData } from 'src/state/scope/Scope.types';
import { ReseedPlanModalOwnProps } from 'src/components/Mfp/Reseed/ReseedPlanModal';
import { keyToCommand } from 'src/state/scope/codecs/projections/PlanMetadataToDropdown';
import { importVersion, overlayVersion } from 'src/state/scope/Scope.actions';
import { toast } from 'react-toastify';
import { Command } from 'src/state/scope/codecs/Commands';
import { anchorsFromCommandDetails, getImportCommands, hasCommands } from 'src/state/scope/codecs/Commands.utils';
import {
  useSelectedPlanId,
  useCurrentCommands,
  useSetPlanId,
  useCommandToDropdown,
  useSelectedCommand,
  useHandleChangeCommand,
} from 'src/utils/Component/hooks/PlanManagement.hooks';
import AnchorRadioSelect from 'src/components/AnchorRadioSelect/AnchorRadioSelect';
import { PlanId } from 'src/state/scope/codecs/PlanMetadata';
import { serverScopeMemberFromId } from 'src/components/Mfp/MfpScopeSelector/MfpScopeUtils';
import { useHandleKeyPress } from 'src/utils/Component/hooks/hooks';

export interface ImportFromVersionOwnProps {
  loading: boolean;
}

const mapStateToProps = (state: AppState) => {
  const { settings } = state;
  const dimensionLabel = settings.dimensionLabelProperty;
  const readyScope = getScopeReadyData(state.mfpScope);

  if (!readyScope || isEmpty(readyScope.commands)) {
    return;
  }

  // map it twice, as hasCommands doesn't return the correct type
  // filter so that we don't get empty sets of commands
  const commands = readyScope.commands.map(getImportCommands).filter(hasCommands).map(getImportCommands);

  return {
    commands,
    dimensionLabel,
    overlayOptions: readyScope.overlayOptions,
    scope: readyScope,
    anchor: serverScopeMemberFromId(anchorsFromCommandDetails(commands), readyScope.mainConfig.members),
    isMultiScope: readyScope.isMultiScope,
    entries: settings.entriesByKey,
  };
};

const mapDispatchToProps = (dispatch: AppThunkDispatch) => {
  return {
    handleImportVersion: async (seedCommand: Command['command']) => {
      // return control to caller for proper error handling
      return dispatch(importVersion(seedCommand)).then((resp) => resp);
    },
    handleOverlayVersion: (overlayId: string, applyTo: PlanId) => {
      return dispatch(overlayVersion(overlayId, applyTo));
    },
  };
};

export type ImportFromVersionProps = ReseedPlanModalOwnProps &
  ImportFromVersionOwnProps &
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps>;

function ImportFromVersion(props: ImportFromVersionProps) {
  const { commands, onCancel, onSubmit, scope, handleImportVersion, dimensionLabel, anchor, isMultiScope, entries } =
    props;

  const [mutationPending, setMutationPending] = useState(false);
  // ddOpen used to control the old Semantic dropdown's open/close, don't believe its needed anymore but left in place.
  const [ddOpen, setDdOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const prevOptions = useRef<typeof commands>();

  // All your plan/command-based hooks remain
  const [selectedPlanId, setSelectedPlanId] = useSelectedPlanId(commands);
  const currentCommands = useCurrentCommands(commands, selectedPlanId);
  const handleChangePlanId = useSetPlanId(commands, setSelectedPlanId);
  const versionOptions = useCommandToDropdown(commands, selectedPlanId, undefined, entries);
  const [selectedCommand, setSelectedCommand] = useSelectedCommand(currentCommands);
  const handleChangeCommand = useHandleChangeCommand(setSelectedCommand, setDdOpen);

  useEffect(() => {
    if (isEmpty(commands) && isUndefined(anchor)) {
      setLoading(false); // no data, doesn't need to remain loading
      setDdOpen(false);
    } else if (isEmpty(commands)) {
      setLoading(true);
    }
  }, [commands, anchor]);

  // if coming from undef options to new options, open the dd
  // this is why the dd needs to be controlled
  useEffect(() => {
    if (!prevOptions.current && versionOptions && !isEmpty(versionOptions) && !ddOpen && !isUndefined(anchor)) {
      setDdOpen(true);
    }
    prevOptions.current = commands;
  }, [ddOpen, versionOptions, commands, anchor]);

  // Submit logic
  const handleSubmit = useCallback(async () => {
    if (!selectedCommand || !currentCommands) return;
    setMutationPending(true);

    const maybeFoundCommand = keyToCommand(selectedCommand, currentCommands.commands);

    if (!maybeFoundCommand) {
      setMutationPending(false);
      return;
    }
    await handleImportVersion(maybeFoundCommand.command)
      .then((resp) => {
        if (importVersion.rejected.match(resp)) {
          // request failed, throw to catch below and toast and keep modal open
          throw resp.error.message;
        }
        if (!isMultiScope) {
          onSubmit(); // only close modal on success
        }
      })
      .catch(() => {
        toast.error('An error occured during import from version');
        throw new Error('An error occured during import from version');
      })
      .finally(() => setMutationPending(false));
  }, [currentCommands, handleImportVersion, isMultiScope, onSubmit, selectedCommand]);

  const handleOnSubmit = useCallback(() => {
    handleSubmit();
  }, [handleSubmit]);

  const handleEnterPress = useHandleKeyPress(handleSubmit);

  return (
    <div className="import-version">
        <Paper elevation={2}>
          <div style={{ marginBottom: '16px', padding: '16px' }}>Select a version to import into your WP</div>
        </Paper>

        <div>
          {scope && dimensionLabel && anchor ? (
            <AnchorRadioSelect
              labelDimenion={dimensionLabel}
              anchor={anchor}
              onUpdateAnchorSelections={handleChangePlanId}
            />
          ) : null}
        </div>

        <Autocomplete
          data-qa="import-version-dropdown"
          className="autoComplete"
          loading={loading}
          fullWidth
          options={versionOptions || []}
          getOptionLabel={(option) => option.text || ''}
          value={versionOptions?.find((opt) => opt.value === selectedCommand) || null}
          onChange={(evt, newValue) => {
            if (newValue) {
              handleChangeCommand(evt, newValue);
            }
          }}
          onKeyPress={handleEnterPress}
          renderInput={(params) => <TextField {...params} variant="outlined" />}
        />
      <CardActions>
        <Button className="button" variant="contained" onClick={onCancel} style={{ marginRight: '0.5rem' }}>
          Close
        </Button>
        <Button
          variant="contained"
          color="primary"
          className="import-version-modal-button button"
          data-qa="version-import-btn-import"
          // MUI doesn't have a 'loading' prop, so disable if loading or pending
          disabled={isUndefined(anchor) || loading || mutationPending}
          onClick={handleOnSubmit}
          onKeyPress={handleEnterPress}
        >
          {mutationPending || loading ? 'Submitting...' : 'Submit'}
        </Button>
      </CardActions>
    </div>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(ImportFromVersion);
